import styled from "@emotion/styled"
import { Box, Tab, Tabs } from "@mui/material"
import { useLocale } from "next-intl"
import { useState } from "react"
import { useInView } from "react-intersection-observer"
import { Swiper, SwiperSlide } from "swiper/react"
import colors from "theme/colors"
import { Container, Mobile, NewText, TransitionUpComponent } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { delayTime, useInViewConfig } from "ui/transitions/utils"
import { extractImage, extractProperty } from "utils"
import CheckmarkIcon from "../../../../packages/ui/icons/CheckmarkIcon"

const StyledContainer = styled.div`
  margin-top: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 60px;
  }
`

const StyledTopContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 16px;
  gap: 16px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    margin: 0 20px;
  }
`

const StyledTabContainer = styled.div``

const StrategyBoxContainer = styled.div`
  position: relative;
`

const StrategyBoxTitleContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-left: 20px;
    min-height: 80px;
  }
`

const StyledBlock = styled.div`
  padding: 16px;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  min-height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* align-items: center; */
`

const StyledIT = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 16px;
  margin-top: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 0 20px;
    margin-top: 24px;
  }
`

const StyledCheckmarkContainer = styled.div`
  display: flex;
  //align-items: center;
  gap: 8px;
`

const StyledPaginatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

const StyledBulletsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 0 4px;
`

const StyledBullet = styled.div<{ active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin: 0 2px;
  cursor: pointer;
  border: 1px solid black;
  border-width: ${(props) => (props.active ? 0 : "1px")};
  background-color: ${(props) => (props.active ? colors.propBlue : "transparant")};
`

const StyledBottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0px;
  margin-bottom: 32px;
`

const StyledTextGap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const IconBox = styled.div`
  width: 22px;
  height: 22px;
  margin-top: 7px;
`
const StyledSwiper = styled(Swiper)`
  padding-bottom: 32px;
  padding-top: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 40px;
    padding-top: 40px;
  }
`
type Props = {
  data: any
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
  data: any
}

function CustomTabPanel(props: TabPanelProps) {
  const { data, value, index, ...other } = props
  const [swiperRef, setSwiperRef] = useState<any>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const locale = useLocale()
  const { ref, inView } = useInView(useInViewConfig)
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      ref={ref}
      {...other}
    >
      {value === index && (
        <Box style={{ position: "relative" }}>
          <StyledSwiper
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              1040: {
                slidesPerView: 3,
              },
            }}
            onSwiper={setSwiperRef}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.activeIndex)
            }}
          >
            {data.map((it: any, as: number) => {
              const letterIcon = extractImage(it, "letterIcon")
              const title = extractProperty(it, "title")
              const blocks = extractProperty(it, "esgStrategyListItem")
              const checkmarkColor = extractProperty(it, "color")
              const rowDelay = delayTime + as * 0.3
              return (
                <SwiperSlide virtualIndex={index} key={as.toString()}>
                  <StrategyBoxContainer>
                    <StrategyBoxTitleContainer>
                      <ImageWithAlt
                        image={letterIcon}
                        style={{ width: "24px", height: "24px" }}
                        width={24}
                        height={24}
                      />
                      <NewText
                        color={colors.black}
                        component="p"
                        desktopFontSize={32}
                        desktopLineHeight="40px"
                        mobileFontSize={24}
                        mobileLineHeight="28px"
                      >
                        {title}
                      </NewText>
                    </StrategyBoxTitleContainer>
                    <StyledIT>
                      {blocks.map((block: any, k: number) => {
                        const blockTitle = extractProperty(block, "title")
                        const blockText = extractProperty(block, "text")

                        return (
                          <TransitionUpComponent
                            key={k.toString()}
                            inView={inView}
                            delay={rowDelay + k * 0.1}
                          >
                            {(transitionStyles) => (
                              <StyledBlock
                                style={{
                                  minHeight: locale === "he" ? "96px" : "118px",
                                  ...transitionStyles,
                                }}
                              >
                                <StyledCheckmarkContainer>
                                  <IconBox>
                                    <CheckmarkIcon fill={`#${checkmarkColor}`} />
                                  </IconBox>

                                  <StyledTextGap>
                                    <NewText
                                      color={colors.black}
                                      component="p"
                                      desktopFontSize={16}
                                      desktopLineHeight="23px"
                                      desktopFontWeight="600"
                                      mobileFontSize={14}
                                      mobileLineHeight="20px"
                                    >
                                      {blockTitle}
                                    </NewText>
                                    <NewText
                                      color={colors.black}
                                      component="p"
                                      desktopFontSize={16}
                                      desktopLineHeight="23px"
                                      mobileFontSize={14}
                                      mobileLineHeight="20px"
                                    >
                                      {blockText}
                                    </NewText>
                                  </StyledTextGap>
                                </StyledCheckmarkContainer>
                              </StyledBlock>
                            )}
                          </TransitionUpComponent>
                        )
                      })}
                    </StyledIT>
                  </StrategyBoxContainer>
                </SwiperSlide>
              )
            })}
          </StyledSwiper>
          <Mobile>
            <StyledBottomContainer>
              <StyledPaginatorContainer>
                <StyledBulletsContainer>
                  {data.map((_: any, index: number) => (
                    <StyledBullet
                      key={index.toString()}
                      onClick={() => {
                        swiperRef.slideTo(index)
                      }}
                      active={index === activeIndex}
                    />
                  ))}
                </StyledBulletsContainer>
              </StyledPaginatorContainer>
            </StyledBottomContainer>
          </Mobile>
        </Box>
      )}
    </div>
  )
}

function NewStrategyMain({ data }: Props) {
  const title = extractProperty(data, "title")
  const strategyBoxes = extractProperty(data, "strategyBoxes")

  const [value, setValue] = useState(0)
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  return (
    <Container forSwiper>
      <StyledContainer>
        <StyledTopContainer>
          <NewText
            color={colors.black}
            component="h2"
            desktopFontSize={40}
            desktopLineHeight="44px"
            mobileFontSize={32}
            mobileLineHeight="36px"
          >
            {title}
          </NewText>
          <Tabs
            sx={{
              backgroundColor: "#F7F7F7",
              borderRadius: "200px",
              padding: "8px",
              borderBottom: "none",
              width: { xs: "216px", md: "236px" },
              gap: "8px",
              display: "flex",
              justifyContent: "space-between",
            }}
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            {strategyBoxes?.map((strategyBox: any, index: number) => {
              const boxTitle = extractProperty(strategyBox, "title")
              return (
                <Tab
                  key={index.toString()}
                  sx={{
                    background: value === index ? "#FFF" : "#F7F7F7",
                    borderRadius: "200px",
                    padding: "8px 16px",
                    marginInlineStart: index !== 0 ? "8px" : "0px",
                    width: { xs: "96px", md: "106px" },
                  }}
                  label={
                    <NewText
                      component="p"
                      desktopFontSize={16}
                      desktopLineHeight="23px"
                      mobileFontSize={14}
                      mobileLineHeight="20px"
                      desktopFontWeight={value === index ? "600" : "400"}
                      color={value === index ? colors.black : colors.gray}
                    >
                      {boxTitle}
                    </NewText>
                  }
                  {...a11yProps(0)}
                />
              )
            })}
          </Tabs>
        </StyledTopContainer>
        <StyledTabContainer>
          {strategyBoxes?.map((strategyBox: any, index: number) => {
            const boxTitle = extractProperty(strategyBox, "title")
            const data = extractProperty(strategyBox, "strategyBoxes")
            return (
              <CustomTabPanel key={index.toString()} data={data} value={value} index={index}>
                {boxTitle}
              </CustomTabPanel>
            )
          })}
        </StyledTabContainer>
      </StyledContainer>
    </Container>
  )
}

export default NewStrategyMain
